import { BasicButton } from "@goodgamestudios/asset-globals";
import { TextFieldHelper } from "@goodgamestudios/core";
import { BasicPanel } from "@goodgamestudios/core";
import { BasicModel } from "@goodgamestudios/core";
import { BasicDialogHandler } from "@goodgamestudios/core";
import { BasicCustomCursor } from "@goodgamestudios/core";
import { Timer } from "@goodgamestudios/createjs-utilities";
import { TextFormat } from "@goodgamestudios/createjs-utilities";
import { ColorTransform } from "@goodgamestudios/createjs-utilities";
import { int } from "@goodgamestudios/math-utilities";

import { getLogger } from "@goodgamestudios/logger";

import { SFConstants } from "../../constants/SFConstants";
import { Balancing } from "../../constants/shared/Balancing";
import { Poker2SoundController } from "../../controller/Poker2SoundController";
import { Poker2AntiCheatEvent } from "../../event/Poker2AntiCheatEvent";
import { Poker2BuddyEvent } from "../../event/Poker2BuddyEvent";
import { Poker2ChatEvent } from "../../event/Poker2ChatEvent";
import { Poker2DialogEvent } from "../../event/Poker2DialogEvent";
import { Poker2OnTableEvent } from "../../event/Poker2OnTableEvent";
import { Poker2TournamentDataEvent } from "../../event/Poker2TournamentDataEvent";
import { Poker2TutorialEvent } from "../../event/Poker2TutorialEvent";
import { Poker2UserEvent } from "../../event/Poker2UserEvent";
import { ChipsearnerVO } from "../../gamevo/ChipsearnerVO";
import { GiftsVO } from "../../gamevo/GiftsVO";
import { LobbyTablesVO } from "../../gamevo/LobbyTablesVO";
import { SeatVO } from "../../gamevo/SeatVO";
import { ShowdownCardsVO } from "../../gamevo/ShowdownCardsVO";
import { WinnerVO } from "../../gamevo/WinnerVO";
import { Poker2Model } from "../../model/Poker2Model";
import { PlayerStateViewComponent } from "../components/playerContainer/PlayerStateViewComponent";
import { TurnTimerComponent } from "../components/playerContainer/TurnTimerComponent";
import { Poker2TableAvatarDialogProperties } from "../dialogs/onTable/Poker2TableAvatarDialogProperties";
import { Poker2TakeSeatDialogProperties } from "../dialogs/onTable/Poker2TakeSeatDialogProperties";
import { Poker2FadingDialogProperties } from "../dialogs/Poker2FadingDialogProperties";
import { Poker2SendGiftDialogProperties } from "../dialogs/Poker2SendGiftDialogProperties";
import { CardHelper } from "../helper/CardHelper";
import { Poker2MovieClipHelper } from "../helper/Poker2MovieClipHelper";
import { PokerTextHelper } from "../helper/PokerTextHelper";
import { GetXPAniHelperController } from "./onTable/animationHelper/GetXPAniHelperController";
import { Poker2Screen } from "./Poker2Screen";

import { NumberStringHelper } from "../../helper/NumberStringHelper";

import DisplayObject = createjs.DisplayObject;
import MovieClip = createjs.MovieClip;
import Container = createjs.Container;
import MouseEvent = createjs.MouseEvent;
import TimerEvent = createjs.TimerEvent;
import Rectangle = createjs.Rectangle;

const logger = getLogger("Poker2OnTableScreen");

export class Poker2OnTableScreen extends Poker2Screen {
    public static NAME: string = "Poker2OnTableScreen";
    public static MAX_SEATS: number = 9;
    private static AVATAR_SCALE_FACTOR: number = 0.7;

    private FLOP: any[] = ["Kd", "9h", "As"];
    private TURN: any[] = ["Jh"];
    private RIVER: any[] = ["Kc"];

    private CARD_WIDTH: number = 70;
    private CARD_SPACE: number = 50;
    private WINNER_CARD_HEIGHT: number = 70;
    private WINNER_CARD_WIDTH: number = 40;
    private CHIP_SCALE: number = 0.2;
    private TWO_SEC_DELAY: number = 2000;

    private STATUS_PLAYER_TURN: number = 2;

    private currentPlayerSeat: number = 0;
    private aniHelper: AniTableHelper;
    private cashPot: number = NaN;
    private onTableHelper: OnTableHelper;
    private currentIndexCounter: number = 0;
    private showdownTimer: Timer;
    private blockDialogTimer: Timer;
    private _isPlayerLonelyTimer: Timer;
    private _winAnimationIsPlaying: boolean = false;
    private _resetPlayerActionState: boolean = false;
    private _winningTimelineAni: Poker2MovieClipHelper;
    private _winningAnimations: any[];

    constructor(disp: DisplayObject) {
        CONSTRUCTOR_HACK;
        super(disp);
    }

    protected init(): void {
        super.init();
        this.onTableHelper = new OnTableHelper(this.onTableScreen.mc_table);
        this.aniHelper = new AniTableHelper();
        this.controller.addEventListener("changelayoutstate", this.bindFunction(this.onChangeLayoutState));

        // set pot text
        var potTitle: string = Poker2Model.languageData.getTextById("lobby_pot").toUpperCase();
        this.onTableScreen.mc_table.mc_pot.tf_pot_title_top.text = potTitle;
        this.onTableScreen.mc_table.mc_pot.tf_pot_title_top.visible = false;
        this.onTableScreen.mc_table.mc_pot.tf_pot_title_bottom.text = potTitle;

        this._winningAnimations = [];
        this._getXPAniHelperController = new GetXPAniHelperController();
        this._hotspotAniHelperController = new HotspotAniHelperController();

        this.onTableScreen.mc_tableBlindView.bg.doCache(0, Poker2LayoutManager.GAME_SCALE_FACTOR);
    }

    public show(): void {
        super.show();
        this.onChangeFullscreen();
        this.onTableHelper.clearDealer();
        this.controller.addEventListener("onchangefullscreen", this.bindFunction(this.onChangeFullscreen));
        this.controller.addEventListener("changeCurrentTableData", this.bindFunction(this.onChangeTableData));
        this.controller.addEventListener("startgame", this.bindFunction(this.onStartGame));
        this.controller.addEventListener("sitDown", this.bindFunction(this.onSitDownAtTable));
        this.controller.addEventListener("standUp", this.bindFunction(this.onStandUp));
        this.controller.addEventListener("playerLevelUp", this.bindFunction(this.onPlayerLevelUp));
        this.controller.addEventListener("playerAction", this.bindFunction(this.onPlayerAction));
        this.controller.addEventListener("changeDealer", this.bindFunction(this.onChangeDealer));
        this.controller.addEventListener("myplayerTurn", this.bindFunction(this.onMyPlayerTurn));
        this.controller.addEventListener("nextplayer", this.bindFunction(this.onNextPlayer));
        this.controller.addEventListener("nextRound", this.bindFunction(this.onNextRound));
        this.controller.addEventListener("playerJoin", this.bindFunction(this.onPlayerJoin));
        this.controller.addEventListener("playerLeave", this.bindFunction(this.onPlayerLeave));
        this.controller.addEventListener("communitycards", this.bindFunction(this.onCommunityCards));
        this.controller.addEventListener("showdown", this.bindFunction(this.onShowdown));
        this.controller.addEventListener("getGift", this.bindFunction(this.onGetGift));
        this.controller.addEventListener("tournamentEnd", this.bindFunction(this.onTournamentEnd));
        this.controller.addEventListener("changeZombie", this.bindFunction(this.onChangeZombie));
        this.controller.addEventListener("tournamentSetBlind", this.bindFunction(this.onTournamentSetBlind));
        this.controller.addEventListener("changePlayerAvatar", this.bindFunction(this.onShowPlayerInfo));
        this.controller.addEventListener("playercards", this.bindFunction(this.onPlayerCards));
        this.controller.addEventListener("changeUserXP", this.bindFunction(this.onGetXP));
        this.controller.addEventListener("onEmptyTable", this.bindFunction(this.onTableEmpty));
        this.controller.addEventListener("addfriend", this.bindFunction(this.onAddFriend));
        this.controller.addEventListener("givechips", this.bindFunction(this.onGiveChips));
        this.controller.addEventListener("resetgame", this.bindFunction(this.onResetGame));
        this.controller.addEventListener("unlockJoinButtons", this.bindFunction(this.onUnlockJoinBtns));
        this.controller.addEventListener("bookSeat", this.bindFunction(this.onBookSeat));
        this.controller.addEventListener("bookedSeat", this.bindFunction(this.onBookedSeat));
        this.controller.addEventListener("bookReleased", this.bindFunction(this.onBookReleased));
        this.controller.addEventListener("showRabbitCam", this.bindFunction(this.onShowRabbitCam));
        this.controller.addEventListener("hideRabbitCam", this.bindFunction(this.onHideRabbitCam));
        this.controller.addEventListener("playRabbitCam", this.bindFunction(this.onPlayRabbitCam));
        if (this.tutorialController.isActive) {
            switch (this.tutorialController.tutorialState) {
                case "tut_step4_freeSeat":
                    this.layoutManager.tutPanel.showTutorialArrow(this.onTableScreen.btn_seat0);
                    this.layoutManager.tutPanel.hideSpotlight();
                    break;
            }
        }

        for (var i = 0; i < 9; i++) {
            this.onTableScreen["mc_player" + i].mc_giftholder.addEventListener(MouseEvent.CLICK, this.bindFunction(this.onPlayerGiftClick));
            this.onTableScreen["mc_player" + i].btn_gift.addEventListener(MouseEvent.CLICK, this.bindFunction(this.onPlayerGiftClick));
            this.onTableScreen["mc_player" + i].mc_holder.addEventListener(MouseEvent.CLICK, this.bindFunction(this.onPlayerClick));
            this.onTableScreen["mc_player" + i].mc_holder.addEventListener(MouseEvent.ROLL_OVER, this.bindFunction(this.onRollOver));
            this.onTableScreen["mc_player" + i].mc_holder.addEventListener(MouseEvent.ROLL_OUT, this.bindFunction(this.onRollOut));
        }
    }

    public hide(): void {
        this.controller.removeEventListener("givechips", this.bindFunction(this.onGiveChips));
        this.controller.removeEventListener("addfriend", this.bindFunction(this.onAddFriend));
        this.controller.removeEventListener("onEmptyTable", this.bindFunction(this.onTableEmpty));
        this.controller.removeEventListener("changeUserXP", this.bindFunction(this.onGetXP));
        this.controller.removeEventListener("onchangefullscreen", this.bindFunction(this.onChangeFullscreen));
        this.controller.removeEventListener("changeCurrentTableData", this.bindFunction(this.onChangeTableData));
        this.controller.removeEventListener("sitDown", this.bindFunction(this.onSitDownAtTable));
        this.controller.removeEventListener("standUp", this.bindFunction(this.onStandUp));
        this.controller.removeEventListener("startgame", this.bindFunction(this.onStartGame));
        this.controller.removeEventListener("playerLevelUp", this.bindFunction(this.onPlayerLevelUp));
        this.controller.removeEventListener("playerAction", this.bindFunction(this.onPlayerAction));
        this.controller.removeEventListener("changeDealer", this.bindFunction(this.onChangeDealer));
        this.controller.removeEventListener("myplayerTurn", this.bindFunction(this.onMyPlayerTurn));
        this.controller.removeEventListener("nextplayer", this.bindFunction(this.onNextPlayer));
        this.controller.removeEventListener("nextRound", this.bindFunction(this.onNextRound));
        this.controller.removeEventListener("playerJoin", this.bindFunction(this.onPlayerJoin));
        this.controller.removeEventListener("playerLeave", this.bindFunction(this.onPlayerLeave));
        this.controller.removeEventListener("communitycards", this.bindFunction(this.onCommunityCards));
        this.controller.removeEventListener("showdown", this.bindFunction(this.onShowdown));
        this.controller.removeEventListener("getGift", this.bindFunction(this.onGetGift));
        this.controller.removeEventListener("tournamentEnd", this.bindFunction(this.onTournamentEnd));
        this.controller.removeEventListener("changeZombie", this.bindFunction(this.onChangeZombie));
        this.controller.removeEventListener("tournamentSetBlind", this.bindFunction(this.onTournamentSetBlind));
        this.controller.removeEventListener("changePlayerAvatar", this.bindFunction(this.onShowPlayerInfo));
        this.controller.removeEventListener("playercards", this.bindFunction(this.onPlayerCards));
        this.controller.removeEventListener("resetgame", this.bindFunction(this.onResetGame));
        this.controller.removeEventListener("unlockJoinButtons", this.bindFunction(this.onUnlockJoinBtns));
        this.controller.removeEventListener("bookSeat", this.bindFunction(this.onBookSeat));
        this.controller.removeEventListener("bookedSeat", this.bindFunction(this.onBookedSeat));
        this.controller.removeEventListener("bookReleased", this.bindFunction(this.onBookReleased));
        this.controller.removeEventListener("showRabbitCam", this.bindFunction(this.onShowRabbitCam));
        this.controller.removeEventListener("hideRabbitCam", this.bindFunction(this.onHideRabbitCam));
        this.controller.removeEventListener("playRabbitCam", this.bindFunction(this.onPlayRabbitCam));
        this.killIsPlayerTimer();
        this.killWinningTimelineAni();
        this.stopTurnTimerAnimations();
        this._getXPAniHelperController.kill();
        this._hotspotAniHelperController.kill();
        if (this._rabbitCam) {
            this._rabbitCam.kill();
        }

        for (var i = 0; i < 9; i++) {
            this.onTableScreen["mc_player" + i].mc_giftholder.removeEventListener(MouseEvent.CLICK, this.bindFunction(this.onPlayerGiftClick));
            this.onTableScreen["mc_player" + i].btn_gift.removeEventListener(MouseEvent.CLICK, this.bindFunction(this.onPlayerGiftClick));
            this.onTableScreen["mc_player" + i].mc_holder.removeEventListener(MouseEvent.CLICK, this.bindFunction(this.onPlayerClick));
            this.onTableScreen["mc_player" + i].mc_holder.removeEventListener(MouseEvent.ROLL_OVER, this.bindFunction(this.onRollOver));
            this.onTableScreen["mc_player" + i].mc_holder.removeEventListener(MouseEvent.ROLL_OUT, this.bindFunction(this.onRollOut));
        }

        super.hide();
    }

    // Stop turn timer animations
    private stopTurnTimerAnimations(): void {
        var i: number = 0;
        var len: number = Poker2Model.onTableData.playersSeatIndex.length;
        var playerContainer: Library.PokerTable.PokerOnTable_Player;
        for (i = 0; i < len; ++i) {
            var seatVO: SeatVO = Poker2Model.onTableData.getSeatVOByIndex(Poker2Model.onTableData.playersSeatIndex[i]);

            playerContainer = this.getPlayerContainer(Poker2Model.onTableData.playersSeatIndex[i]);

            if (playerContainer.turnTimer) {
                ((playerContainer.turnTimer) as TurnTimerComponent).stopAll();
            }
        }
    }

    private killWinningTimelineAni(): void {
        var i: number = 0;
        var len: number = int(this._winningAnimations.length);
        var tempWinningAni: Poker2MovieClipHelper;
        for (i = 0; i < len; ++i) {
            tempWinningAni = this._winningAnimations[i];
            tempWinningAni.kill();
            tempWinningAni = null;
        }
        // reset _winningAnimations array
        this._winningAnimations = [];
    }

    private killIsPlayerTimer(): void {
        if (this._isPlayerLonelyTimer) {
            this._isPlayerLonelyTimer.stop();
            this._isPlayerLonelyTimer.removeEventListener(TimerEvent.TIMER_COMPLETE, this.bindFunction(this.onIsPlayerLonelyTimerComplete));
            this._isPlayerLonelyTimer = null;
        }
    }

    private onChangeFullscreen(e: Poker2DialogEvent = null): void {
        if (this.layoutManager.isActiveFullscreen && !Poker2Model.userData.isGuest())
            this.layoutManager.showDialog("Poker2FadingDialog", new Poker2FadingDialogProperties(Poker2Model.languageData.getTextById("table_noChat_fullscreen")));
    }

    public destroy(): void {
        this.controller.removeEventListener("changelayoutstate", this.bindFunction(this.onChangeLayoutState));

        this.killIsPlayerTimer();
        this.killWinningTimelineAni();
        this.stopTurnTimerAnimations();
        this._getXPAniHelperController.kill();
        this._hotspotAniHelperController.kill();
        if (this._rabbitCam) {
            this._rabbitCam.kill();
        }

        if (this.showdownTimer) {
            this.showdownTimer.removeEventListener(TimerEvent.TIMER, this.bindFunction(this.showPlayerCards));
            this.showdownTimer.removeEventListener(TimerEvent.TIMER_COMPLETE, this.bindFunction(this.onShowPlayerCardsComplete));
            this.showdownTimer.removeEventListener(TimerEvent.TIMER_COMPLETE, this.bindFunction(this.onShowAllinComplete));
        }
        super.destroy();
    }

    protected onRollOver(event: MouseEvent): void {
        this.layoutManager.customCursor.setCursorType(BasicCustomCursor.CURSOR_CLICK);
    }

    protected onRollOut(event: MouseEvent): void {
        this.layoutManager.customCursor.setCursorType(BasicCustomCursor.CURSOR_ARROW);
    }

    protected onTutorialEvent(event: Poker2TutorialEvent): void {
        const tutHand: number = 400;
        const tutSmal: number = 25;
        const tutBig: number = 50;

        if (this.tutorialController.isActive) {
            console.log("tutorialController.tutorialState " + this.tutorialController.tutorialState);
            switch (this.tutorialController.tutorialState) {
                case "tut_step4_freeSeat":
                    this.layoutManager.tutPanel.showTutorialArrow(this.onTableScreen.btn_seat0);
                    break;
                case "tut_step5_buyIn":
                    this.hidePlayerSeat(0);
                    this.joinButtonHelper(true);
                    break;
                case "tut_step6_dealerButton":
                    this.showPlayerAction([8, 0, tutHand, 3]);
                    break;
                case "tut_step7_smallBlind":
                    this.showPlayerAction([8, 0, tutHand, 2]);
                    this.onTableHelper.drawBet(2, 0, this.aniHelper);
                    this.onTableHelper.drawBet(1, tutSmal, this.aniHelper);
                    break;
                case "tut_step8_bigBlind":
                    this.onTableHelper.drawBet(2, tutBig, this.aniHelper);
                    break;
                case "tut_step9_holeCards":
                    this.showPlayerAction([8, 0, tutHand, 3]);
                    break;
                case "tut_step12_actionHelp2":
                    this.showPlayerAction([8, 0, tutHand, 0]);
                    this.showPlayerAction([8, 0, tutHand - tutSmal, 1]);
                    break;
                case "tut_step13_playerActionCall":
                    this.showPlayerAction([8, tutSmal, tutHand - tutBig, 1]);
                    this.showPlayerAction([8, 0, tutHand, 0]);
                    break;
                case "tut_step14_timoCall":
                    this.showPlayerAction([8, tutBig, tutHand - tutBig, 2]);
                    break;
                case "tut_step15_oliverCheck":
                    this.restoreLastPot(0);
                    this.onTableHelper.drawBet(0, tutBig, this.aniHelper);
                    this.onTableHelper.drawBet(1, tutBig, this.aniHelper);
                    break;
                case "tut_step16_pot":
                    this.onTableHelper.clearCommunityCards();
                    Poker2Model.onTableData.currentTable.communityCards = new Array();
                    break;
                case "tut_step17_flop":
                    this.onTableHelper.clearCommunityCards();
                    this.onTableHelper.drawTutorialCommunityCards(this.FLOP, this.aniHelper, true);
                    this.showPlayerAction([3, 0, tutHand - tutBig, 1]);
                    break;
                case "tut_step19_oliverCheck":
                    this.showPlayerAction([3, 0, tutHand - tutBig, 0]);
                    break;
                case "tut_step22_playerActionCheck":
                    this.showPlayerAction([7, 0, tutHand - tutBig, 0]);
                    if (this.onTableHelper.tableMC.mc_cardholder.numChildren > 3) {
                        this.onTableHelper.clearCommunityCards();
                        this.onTableHelper.drawTutorialCommunityCards(this.FLOP, this.aniHelper, false);
                    }
                    break;
                case "tut_step23_turn":
                    this.showPlayerAction([2, 0, tutHand - tutBig, 1]);
                    if (this.onTableHelper.tableMC.mc_cardholder.numChildren > 3) {
                        this.onTableHelper.clearCommunityCards();
                        this.onTableHelper.drawTutorialCommunityCards(this.FLOP, this.aniHelper, false);
                        this.onTableHelper.drawTutorialCommunityCards(this.TURN, this.aniHelper, true);
                        return;
                    }
                    this.onTableHelper.drawTutorialCommunityCards(this.TURN, this.aniHelper, true);
                    break;
                case "tut_step24_timoRaise":
                    this.showPlayerAction([4, tutBig, tutHand - tutBig - tutSmal, 1]);
                    this.showPlayerAction([2, 0, tutHand - tutBig, 2]);
                    break;
                case "tut_step25_oliverFold":
                    this.showPlayerAction([2, 0, tutHand - tutBig, 0]);
                    break;
                case "tut_step26_playerActionCall":
                    this.restoreLastPot(3 * tutBig);
                    this.showPlayerAction([7, 0, tutHand - tutBig, 0]);
                    this.showPlayerAction([4, tutBig, tutHand - 2 * tutBig, 1]);
                    if (this.onTableHelper.tableMC.mc_cardholder.numChildren > 4) {
                        this.onTableHelper.clearCommunityCards();
                        this.onTableHelper.drawTutorialCommunityCards(this.FLOP.concat(this.TURN), this.aniHelper, false);
                    }
                    break;
                case "tut_step27_river":
                    this.showPlayerAction([3, 0, tutHand - 2 * tutBig, 0]);
                    this.showPlayerAction([4, tutBig, tutHand - 2 * tutBig, 1]);
                    if (this.onTableHelper.tableMC.mc_cardholder.numChildren >= 5) {
                        this.onTableHelper.clearCommunityCards();
                        this.onTableHelper.drawTutorialCommunityCards(this.FLOP.concat(this.TURN), this.aniHelper, false);
                        this.onTableHelper.drawTutorialCommunityCards(this.RIVER, this.aniHelper, true);
                        return;
                    }
                    this.onTableHelper.drawTutorialCommunityCards(this.RIVER, this.aniHelper, true);
                    break;
                case "tut_step28_timoCheck":
                    this.showPlayerAction([3, 0, tutHand - 2 * tutBig, 0]);
                    break;
                case "tut_step29_playerActionRaise":
                    this.showPlayerAction([7, 0, tutHand - 2 * tutBig, 0]);
                    this.showPlayerAction([2, 0, tutHand - 2 * tutBig, 1]);
                    break;
                case "tut_step30_timoReraise":
                    this.showPlayerAction([4, tutBig, tutHand - 2 * tutBig, 0]);   // 100+200
                    this.showPlayerAction([4, 2 * tutBig, tutHand - 3 * tutBig, 1]); // 50+250
                    break;
                case "tut_step31_playerActionAllin":
                    this.showPlayerAction([7, tutBig, tutHand - 3 * tutBig, 0]);
                    this.showPlayerAction([4, 2 * tutBig, tutHand - 4 * tutBig, 1]);
                    break;
                case "tut_step32_timoAllin":
                    this.restoreLastPot(5 * tutBig);
                    this.onTableHelper.drawTutorialCommunityCards(this.FLOP.concat(this.TURN.concat(this.RIVER)), this.aniHelper, false);
                    break;
            }
            return;

        }
    }

    private restoreLastPot(pot: number): void {
        this.cashPot = pot;
        Poker2Model.onTableData.showdownPot = pot;
        this.onTableScreen.mc_table.mc_pot.txt_amount.text = (pot > 0) ? NumberStringHelper.groupString(pot, Poker2Model.languageData.getTextById.bind(Poker2Model.languageData)) : "";

        if (this.onTableScreen.mc_table.mc_pot.mc_holder.numChildren) {
            var currentPotChips: Library.PokerTable.Chips = castAs<Library.PokerTable.Chips>(this.onTableScreen.mc_table.mc_pot.mc_holder.getChildAt(0), "Chips");
            if (currentPotChips)
                this.aniHelper.setChipsWert2Chips(currentPotChips, pot);
        }
    }

    private onPlayerGiftClick(event: MouseEvent): void {
        if (event.currentTarget && Poker2Model.onTableData.myPlayerPos >= 0) {
            this.layoutManager.showDialog("Poker2SendGiftDialog", new Poker2SendGiftDialogProperties(event.currentTarget.seatId));
        }
    }

    private onPlayerClick(event: MouseEvent): void {
        var container: Library.PokerTable.PokerOnTable_Player = event.currentTarget.parent as Library.PokerTable.PokerOnTable_Player;
        if (container.properties == Poker2Model.onTableData.myPlayerPos) {
            var seatVO: SeatVO = Poker2Model.onTableData.getSeatVOByIndex(container.properties);

            if ((instanceOfClass(Poker2Model.onTableData.currentTable.gameType, "TournamentTypeAbstract")) && seatVO.zombie == 1) {
                BasicModel.smartfoxClient.sendMessage("jzo", [JSON.stringify({id: Poker2Model.tournamentData.selectedTournament})]);
            }
            else if ((instanceOfClass(Poker2Model.onTableData.currentTable.gameType, "SitngoTypeAbstract")) && seatVO.zombie == 1) {
                var paramObject: any = {};
                BasicModel.smartfoxClient.sendMessage("szo", [JSON.stringify(paramObject)]);
            }
            else {
                this.getAvatarCard(container.playerid);
            }
        }
        else {
            this.getAvatarCard(container.playerid);
        }
    }

    protected onClick(event: MouseEvent): void {
        if (this.isLocked)
            return;

        if (this.tutorialController.isActive) {
            switch (this.tutorialController.tutorialState) {
                case "tut_step4_freeSeat":
                    if (event.target == this.onTableScreen.btn_seat0) {
                        this.tutorialController.nextStep();
                    }
                    break;
            }
            return;
        }

        super.onClick(event);

        if (event.target) {
            if (event.target instanceof BasicButton && event.target.enabled && "properties" in event.target) {
                this.bookSeat(event.target.properties);
            }
        }
    }

    private onBookSeat(event: Poker2OnTableEvent): void {
        // show take seat dialog only for default tables
        if (instanceOfClass(Poker2Model.onTableData.currentTable.gameType, "DefaultTableTypeAbstract")) {
            //				BasicDialogHandler.getInstance().registerDialogs( "Poker2TakeSeatDialog", new Poker2TakeSeatDialogProperties( event.params[0], false,event.params[1],event.params[2]),true,BasicDialogHandler.PRIORITY_HIGH);

            //TODO SHOWTABLEPRICE
            this.layoutManager.showDialog("Poker2TakeSeatDialog", new Poker2TakeSeatDialogProperties(event.params[0], true, event.params[1], event.params[2]));
        }
    }
    private onBookedSeat(event: Poker2OnTableEvent): void {
        this.disableSeat(event.params[0]);
    }
    private onBookReleased(event: Poker2OnTableEvent): void {
        if (Poker2Model.onTableData.myPlayerPos == -1) {
            this.releaseSeat(event.params[0]);
            this.joinButtonHelper(true);
            this.checkIfBooked(Poker2Model.onTableData.currentTable.bookedSeats);
        }
    }

    private onShowPlayerInfo(event: Poker2UserEvent): void {
        if (event.params[1] == 1)
            this.layoutManager.showDialog("Poker2TableAvatarDialog", new Poker2TableAvatarDialogProperties(event.params[0]));
    }

    private onGetXP(event: Poker2UserEvent): void {
        if (Poker2Model.onTableData.myPlayerPos >= 0) {
            // start mc
            var playerContainer: Library.PokerTable.PokerOnTable_Player = this.getPlayerContainer(Poker2Model.onTableData.myPlayerPos);

            // xp amount
            var xp: number = Poker2Model.userData.gainedXP;
            xp = (!isNaN(xp)) ? xp : 0;
            var gainedXP: string = NumberStringHelper.groupString(xp, Poker2Model.languageData.getTextById.bind(Poker2Model.languageData));

            // target mc
            var menuPanel: BasicPanel = Poker2LayoutManager.getInstance().getPanelById("Poker2MenuPanel");

            var con: Container = new Container();
            con.name = this._getXPAniHelperController.xpAniId;
            this.layoutManager.showAnimation(con);

            // play get xp animation
            this._getXPAniHelperController.showXPAnimation(gainedXP, playerContainer, menuPanel.disp, con, this.disp);
        }
    }

    private onTournamentEnd(event: Poker2TournamentDataEvent): void {
        this.onTableHelper.clearTable();
        this.resetPlayerContainers(Poker2Model.onTableData.playersSeatIndex);
    }

    private onTournamentSetBlind(event: Poker2TournamentDataEvent): void {
        this.setBlindText(event.params[0]);
    }

    private setBlindText(blind: number): void {
        var smallBlind: string = PokerTextHelper.getShortGroupNumberString(blind);
        var bigBlind: string = PokerTextHelper.getShortGroupNumberString(blind * 2);
        var blindText: string = smallBlind + "/" + bigBlind;
        TextFieldHelper.changeTextFromatSizeByTextWidth(10, this.onTableScreen.mc_tableBlindView.txt_blinds, Poker2Model.languageData.getTextById("create_table_blinds", [blindText]));

        if (Poker2Model.onTableData.currentTable && Poker2Model.onTableData.currentTable.name)
            TextFieldHelper.changeTextFromatSizeByTextWidth(10, this.onTableScreen.mc_tableBlindView.txt_tablename, Poker2Model.onTableData.currentTable.name);
    }

    private onChangeZombie(event: Poker2TournamentDataEvent): void {
        var index: number = int(event.params[1]);
        var seatVO: SeatVO = Poker2Model.onTableData.getSeatVOByIndex(index);
        seatVO.stateGame = int(8);
        this.checkZombieView(index, seatVO, this.getPlayerContainer(index));
        this.updatePlayerContainer(index, seatVO);
    }

    private onPlayerAction(event: Poker2OnTableEvent): void {
        this.showPlayerAction(Poker2Model.onTableData.playerActions);
    }

    private onStandUp(event: Poker2OnTableEvent): void {
        if (Poker2Model.onTableData.myPlayerPos >= 0) {
            var betContainer: MovieClip = this.onTableScreen.mc_table["mc_bet_" + Poker2Model.onTableData.myPlayerPos] as MovieClip;
            this.aniHelper.animationChips2PotOnePlayer(betContainer, this.onTableScreen.mc_table.mc_pot);
            this.hidePlayerSeat(Poker2Model.onTableData.myPlayerPos);
            Poker2Model.onTableData.removeSeat(Poker2Model.onTableData.myPlayerPos);
            Poker2Model.onTableData.myPlayerPos = -1;

            // don't unlock seats if a tournament/sitngo has been finished (see JEN/SENCommand)
            if (event.params[0] && !((typeof event.params[0] === 'number')) && !event.params[0].unlockSeats) {
                return;
            }

            // unlock seats
            this.joinButtonHelper(true);
            this.checkIfBooked(Poker2Model.onTableData.currentTable.bookedSeats);
        }
    }

    private onPlayerLeave(event: Poker2OnTableEvent): void {
        var index: number = int(event.params[0]);
        var seatVO: SeatVO = event.params[1];
        var isMyPlayer: boolean = event.params[2];

        // my own player is leaving
        if (isMyPlayer) {
            if (instanceOfClass(Poker2Model.onTableData.currentTable.gameType, "DefaultTableTypeAbstract")) {
                this.controller.soundController.playSoundEffectByName(Poker2SoundController.SND_LOSE);
            }
            this.joinButtonHelper(false);
            Poker2Model.onTableData.myPlayerPos = -1;
        }
        this.controller.dispatchEvent(new Poker2ChatEvent("addlog", seatVO.playerId, seatVO.userId, Poker2Model.languageData.getTextById("table_chatlog_leave", [seatVO.name]), seatVO.name, false, false));

        var betContainer: MovieClip = this.onTableScreen.mc_table["mc_bet_" + index] as MovieClip;
        this.aniHelper.animationChips2PotOnePlayer(betContainer, this.onTableScreen.mc_table.mc_pot);
        this.hidePlayerSeat(index);

        this.startIsPlayerIsLonelyTimer();
    }

    private startIsPlayerIsLonelyTimer(defaultDelay: number = 1600): void {
        this.killIsPlayerTimer();
        this._isPlayerLonelyTimer = new Timer(defaultDelay, 1);
        this._isPlayerLonelyTimer.addEventListener(TimerEvent.TIMER_COMPLETE, this.bindFunction(this.onIsPlayerLonelyTimerComplete));
        this._isPlayerLonelyTimer.start();
    }

    protected onIsPlayerLonelyTimerComplete(event: TimerEvent): void {
        // kill timer
        this.killIsPlayerTimer();

        // Check if winning animation of the player action state is currently playing.
        // If this is true, reset player's state when the animation has finished.
        if (this._winAnimationIsPlaying) {
            // win animation is playing, dont't interrupt, but call the checkIfOnePlayerIsLeft() method
            // after the win animation has finished
            this._resetPlayerActionState = true;
            return;
        }

        this.checkIfOnePlayerIsLeft();
    }

    /**
     * Checks every time a player leaves the table (after a certain delay time) whether there is only one player left on the table.
     * In this case this last player have the be the winner of the last round and its cards shouldn't be visible all the time until a new player joins.
     * This methods resets the hand cards of the last winner and its state to SeatVO.STILL_WAITING as well as resets the table cards.
     *
     * @param event: TimerEvent of _isPlayerLonelyTimer
     */
    protected checkIfOnePlayerIsLeft(): void {
        // sanity checks
        if (!Poker2Model.onTableData.currentTable || !Poker2Model.onTableData.currentTable.seats) {
            logger.warn("checkIfOnePlayerIsLeft() -> Poker2Model.onTableData.currentTable or Poker2Model.onTableData.currentTable.seats = null");
            return;
        }

        var seats: any[] = Poker2Model.onTableData.currentTable.seats;
        var i: number = 0;
        var len: number = int(seats.length);
        var seatVO: SeatVO;
        var seatedCounter: number = 0;
        for (i = 0; i < len; ++i) {
            if (((seats[i]) as SeatVO).seated) {
                seatVO = seats[i];
                seatedCounter++;
            }
        }

        // sanity check: if more than one seat is engaged, do nothing
        if (seatedCounter > 1) {
            //				logger.warn("checkIfOnePlayerIsLeft() -> seatedCounter > 1 ");
            return;
        }

        // If only one seat is engaged, this player have to be the winner of the last round.
        // Reset the cards on table and the player container of the winner, if he is alone on table.
        if (seatVO) {
            // get playerContainer of the last player
            var playerContainer: Library.PokerTable.PokerOnTable_Player = this.getPlayerContainer(Poker2Model.onTableData.playersSeatIndex[0]);

            if (!playerContainer) {
                logger.warn("checkIfOnePlayerIsLeft() -> playerContainer null: " + playerContainer);
                return;
            }

            // reset table
            this.onTableHelper.clearTable();

            // clear hand cards of the last player
            while (playerContainer.mc_cardholder.numChildren > 0)
                playerContainer.mc_cardholder.removeChildAt(0);

            // set state of the last player to waiting
            this.setPlayerActionbyState(8, playerContainer);
        }
    }

    private onUnlockJoinBtns(event: Poker2OnTableEvent): void {
        this.joinButtonHelper(true);
        this.checkIfBooked(Poker2Model.onTableData.currentTable.bookedSeats);
    }

    private onTableEmpty(event: Poker2OnTableEvent): void {
        this.onTableHelper.clearTable();
    }

    private onAddFriend(event: Poker2BuddyEvent): void {
        var fromIndex: number = int(event.params[0]);
        var toIndex: number = int(event.params[1]);
        var fromPlayerContainer: Library.PokerTable.PokerOnTable_Player = this.getPlayerContainer(fromIndex);
        var toPlayerContainer: Library.PokerTable.PokerOnTable_Player = this.getPlayerContainer(toIndex);
        this.aniHelper.addPlayerCardAnimation(fromPlayerContainer, new Library.PokerUIElements.Anim_Handshake());
        this.aniHelper.addPlayerCardAnimation(toPlayerContainer, new Library.PokerUIElements.Anim_Handshake());
    }

    private onGiveChips(event: Poker2BuddyEvent): void {
        var fromIndex: number = int(event.params[0]);
        var toIndex: number = int(event.params[1]);
        var amount: number = event.params[2];

        var fromPlayerContainer: Library.PokerTable.PokerOnTable_Player = this.getPlayerContainer(fromIndex);
        var toPlayerContainer: Library.PokerTable.PokerOnTable_Player = this.getPlayerContainer(toIndex);

        // open chips configuration dialog
        var giftChipsMc: Library.PokerInterface.GiftChips = new Library.PokerInterface.GiftChips();
        giftChipsMc.icon._1.allowFastGlow = false;
        giftChipsMc.icon._1.doCache();
        // put toPlayerContainer on top of all player containers (z-index)
        toPlayerContainer.parent.addChild(toPlayerContainer);
        // put gift chips mc into toPlayerContainer
        toPlayerContainer.addChild(giftChipsMc);
        this.aniHelper.doGiveChipsAnimation(fromPlayerContainer, toPlayerContainer, giftChipsMc, amount);
    }

    private onPlayerLevelUp(event: Poker2OnTableEvent): void {
        if (event.params && event.params.length > 1) {
            var index: number = int(event.params[0]);
            var level: number = int(event.params[1]);
            var ani: MovieClip = new Library.PokerUIElements.LevelUp_ani();
            ani.scaleX = 0.2;
            ani.scaleY = 0.2;
            var playerSeatVO: SeatVO = Poker2Model.onTableData.getSeatVOByIndex(index);
            var playerContainer: Library.PokerTable.PokerOnTable_Player = this.getPlayerContainer(index);
            playerSeatVO.level = level;
            this.updatePlayerContainer(index, playerSeatVO);
            this.aniHelper.addPlayerCardAnimation(playerContainer, ani);
        }
    }

    /**
     * Spiel zurücksetzen, Sound abspielen
     */
    private onStartGame(event: Poker2OnTableEvent): void {
        this.onTableHelper.clearTable();
        this.controller.soundController.playSoundEffectByName(Poker2SoundController.SND_PAUSE);
        this.resetPlayerContainers(Poker2Model.onTableData.playersSeatIndex);
    }

    /**
     * Wenn Karten ausgeteilt werden, pro Spieler 2x Sound abspielen
     */
    private onPlayerCards(event: Poker2OnTableEvent): void {
        this.controller.soundController.playSoundEffectByName(Poker2SoundController.SND_SPREAD, 2);
    }

    private onShowdown(event: Poker2OnTableEvent): void {

        this.cashPot = Poker2Model.onTableData.showdownPot;
        this.onTableHelper.drawPot(this.cashPot, this.aniHelper, true);
        this.controller.soundController.playSoundEffectByName(Poker2SoundController.SND_POT);
        this.onTableHelper.potAnimation(this.aniHelper);
        this.showShowdown(Poker2Model.onTableData.showdownCards, Poker2Model.onTableData.showdownCommunitycards, Poker2Model.onTableData.showdownWinner, Poker2Model.onTableData.showdownChips);
        this.onTableHelper.clearDealer();
    }

    private onResetGame(event: Poker2OnTableEvent): void {
        this.onTableHelper.clearTable();
    }

    private onSitDownAtTable(event: Poker2OnTableEvent): void {
        this.drawPlayerSeat(event.params[0], Poker2Model.onTableData.myPlayerPos);

        this.showHotspotAnimation();

        this.joinButtonHelper(false);
    }

    private showHotspotAnimation(): void {
        var playerContainer: Library.PokerTable.PokerOnTable_Player = this.getPlayerContainer(Poker2Model.onTableData.myPlayerPos);

        var con: Container = new Container();
        this.layoutManager.showAnimation(con);

        this._hotspotAniHelperController.showHotspotAnimation(playerContainer, con, this.disp);
    }

    private onChangeDealer(event: Poker2OnTableEvent): void {
        this.onTableHelper.drawDealer(Poker2Model.onTableData.currentTable.dealer, this.aniHelper);
    }

    private onMyPlayerTurn(event: Poker2OnTableEvent): void {
        this.controller.soundController.playSoundEffectByName(Poker2SoundController.SND_TURN);
        this.showPlayerStatus(Poker2Model.onTableData.myPlayerPos, this.STATUS_PLAYER_TURN);
    }

    private onNextPlayer(event: Poker2OnTableEvent): void {
        this.showPlayerStatus(Poker2Model.onTableData.currentPlayerIndex, this.STATUS_PLAYER_TURN);
    }

    private onNextRound(event: Poker2OnTableEvent): void {
        this.cashPot = event.params[0];
        this.onTableHelper.drawPot(this.cashPot, this.aniHelper, true);
        this.controller.soundController.playSoundEffectByName(Poker2SoundController.SND_POT);
    }

    private onGetGift(event: Poker2OnTableEvent): void {
        var giftId: number = int(event.params.shift());
        var fromIndex: number = int(event.params.shift());
        var toIndex: number = int(event.params.shift());

        if (toIndex != -1) {
            // an einen bestimmten player senden
            var seat: SeatVO = Poker2Model.onTableData.getSeatVOByIndex(toIndex);
            seat.giftId = giftId;
            this.drawPlayerGift(seat, fromIndex, toIndex);
            this.controller.dispatchEvent(new Poker2ChatEvent("addlog", seat.playerId, seat.userId, Poker2Model.languageData.getTextById("table_chatlog_gift", [seat.name]), seat.name, false, false));
        }
        else {
            // an alle senden
            for (var i: number = 0; i < 9; i++) {
                var seatVO: SeatVO = Poker2Model.onTableData.currentTable.seats[i] as SeatVO;

                if (seatVO.seated) {
                    seatVO.giftId = giftId;
                    this.drawPlayerGift(seatVO, fromIndex, i);
                    this.controller.dispatchEvent(new Poker2ChatEvent("addlog", seatVO.playerId, seatVO.userId, Poker2Model.languageData.getTextById("table_chatlog_gift", [seatVO.name]), seatVO.name, false, false));
                }
            }
        }
    }

    private onPlayerJoin(event: Poker2OnTableEvent): void {
        var seatVO: SeatVO = event.params[0];
        var index: number = int(event.params[1]);
        seatVO.stateGame = int(8);
        Poker2Model.onTableData.addSeat(seatVO, index);
        seatVO.seated = true;
        this.drawPlayerSeat(seatVO, index);

        this.controller.dispatchEvent(new Poker2ChatEvent("addlog", seatVO.playerId, seatVO.userId, Poker2Model.languageData.getTextById("table_chatlog_join", [seatVO.name]), seatVO.name, false, false));
    }

    public static STANDARD_TABLE_GREEN: ColorTransform;
    public static TOURNAMENT_TABLE_BLUE: ColorTransform;
    public static SITNGO_TABLE_GREY: ColorTransform;

    public static STANDARD_TABLE_GREEN_TXT_FORMAT: TextFormat;
    public static TOURNAMENT_TABLE_BLUE_TXT_FORMAT: TextFormat;
    public static SITNGO_TABLE_GREY_TXT_FORMAT: TextFormat;
    public static FALLBACK_FOR_SMALL_RESOLUTION_SCREENSIZE_TXT_FORMAT: TextFormat;
    public static XMAS_TABLE_TXT_FORMAT: TextFormat;
    private _rabbitCam: RabbitCam;
    private _getXPAniHelperController: GetXPAniHelperController;
    private _hotspotAniHelperController: HotspotAniHelperController;

    private onChangeTableData(event: Poker2OnTableEvent): void {
        this.onTableHelper.clearTable();

        var currentTable: LobbyTablesVO = Poker2Model.onTableData.currentTable;

        var color: ColorTransform;
        var format: TextFormat;

        switch (currentTable.gameType) {
            case PokerGameTypes.DEFAULT_TABLE_TYPE_STANDARD:

                if (Poker2Model.eventIsActive && Poker2Model.sessionData.currentEvents && Poker2Model.sessionData.currentEvents[0] == "xmas") {
                    // xmas table bg
                    this.onTableScreen.mc_table.mc_table.gotoAndStop(7);
                    color = null;
                    format = Poker2OnTableScreen.XMAS_TABLE_TXT_FORMAT;
                    this.joinButtonHelper(true);
                    this.checkIfBooked(currentTable.bookedSeats);
                }
                else {
                    // default table bg
                    this.onTableScreen.mc_table.mc_table.gotoAndStop(1);
                    color = Poker2OnTableScreen.STANDARD_TABLE_GREEN;
                    format = Poker2OnTableScreen.STANDARD_TABLE_GREEN_TXT_FORMAT;
                    this.joinButtonHelper(true);
                    this.checkIfBooked(currentTable.bookedSeats);
                }
                break;

            case PokerGameTypes.DEFAULT_TABLE_TYPE_FAST:
                this.onTableScreen.mc_table.mc_table.gotoAndStop(3);

                // fix cache cut-off graphics
                this.onTableScreen.mc_table.mc_table.children[0].doCache();

                color = Poker2OnTableScreen.STANDARD_TABLE_GREEN;
                format = Poker2OnTableScreen.STANDARD_TABLE_GREEN_TXT_FORMAT;
                this.joinButtonHelper(true);
                this.checkIfBooked(currentTable.bookedSeats);
                break;

            case PokerGameTypes.TOURNAMENT_TYPE_SHOOTOUT:
            case PokerGameTypes.TOURNAMENT_TYPE_SHOOTOUT_SPECTATOR:
                this.onTableScreen.mc_table.mc_table.gotoAndStop(2);
                // fix cache cut-off graphics
                this.onTableScreen.mc_table.mc_table.children[0].doCache();
                color = Poker2OnTableScreen.TOURNAMENT_TABLE_BLUE;
                format = Poker2OnTableScreen.TOURNAMENT_TABLE_BLUE_TXT_FORMAT;
                this.joinButtonHelper(false);
                break;

            case PokerGameTypes.TOURNAMENT_TYPE_FREEZEOUT:
            case PokerGameTypes.TOURNAMENT_TYPE_FREEZEOUT_SPECTATOR:
                this.onTableScreen.mc_table.mc_table.gotoAndStop(6);
                // fix cache cut-off graphics
                this.onTableScreen.mc_table.mc_table.children[0].doCache();
                color = Poker2OnTableScreen.STANDARD_TABLE_GREEN;
                format = Poker2OnTableScreen.STANDARD_TABLE_GREEN_TXT_FORMAT;
                this.joinButtonHelper(false);
                break;

            case PokerGameTypes.TOURNAMENT_TYPE_REBUY:
            case PokerGameTypes.TOURNAMENT_TYPE_REBUY_SPECTATOR:
                this.onTableScreen.mc_table.mc_table.gotoAndStop(8);

                // fix cache cut-off graphics
                this.onTableScreen.mc_table.mc_table.children[0].doCache();

                color = Poker2OnTableScreen.STANDARD_TABLE_GREEN;
                format = Poker2OnTableScreen.STANDARD_TABLE_GREEN_TXT_FORMAT;
                this.joinButtonHelper(false);
                break;

            case PokerGameTypes.SITNGO_TYPE_STANDARD:
            case PokerGameTypes.SITNGO_TYPE_STANDARD_SPECTATOR:
                this.onTableScreen.mc_table.mc_table.gotoAndStop(4);
                // fix cache cut-off graphics
                this.onTableScreen.mc_table.mc_table.children[0].doCache();
                color = Poker2OnTableScreen.SITNGO_TABLE_GREY;
                format = Poker2OnTableScreen.SITNGO_TABLE_GREY_TXT_FORMAT;
                this.joinButtonHelper(false);
                break;

            case PokerGameTypes.DEFAULT_TABLE_TYPE_MIN50BB:
                this.onTableScreen.mc_table.mc_table.gotoAndStop(5);
                // fix cache cut-off graphics
                this.onTableScreen.mc_table.mc_table.children[0].doCache();
                color = Poker2OnTableScreen.STANDARD_TABLE_GREEN;
                format = Poker2OnTableScreen.STANDARD_TABLE_GREEN_TXT_FORMAT;
                this.joinButtonHelper(true);
                this.checkIfBooked(currentTable.bookedSeats);
                break;

            default:

                // default table bg
                this.onTableScreen.mc_table.mc_table.gotoAndStop(1);
                color = Poker2OnTableScreen.STANDARD_TABLE_GREEN;
                format = Poker2OnTableScreen.STANDARD_TABLE_GREEN_TXT_FORMAT;
                this.joinButtonHelper(true);
                this.checkIfBooked(currentTable.bookedSeats);
        }

        this.onTableScreen.mc_table.mc_table.potBox.uncache();
        this.onTableScreen.mc_table.mc_table.potBox.uncacheAllChildren();

        // set color of pot box
        if (color) {
            this.adjustPotBoxColor(this.onTableScreen.mc_table.mc_table.potBox, color);
        }

        // if resolution is 800x600 or lower, show the pot text in white to improve the readability
        if (color && this.onTableScreen.stage.stageWidth <= 800 || this.onTableScreen.stage.stageHeight <= 600) {
            format = Poker2OnTableScreen.FALLBACK_FOR_SMALL_RESOLUTION_SCREENSIZE_TXT_FORMAT;
        }

        // set title of current table
        this.onTableScreen.mc_table.mc_pot.tf_pot_title_top.setTextFormat(format);

        // spieler anzeigen
        for (var i: number = 0; i < 9; i++) {
            this.drawPlayerSeat(currentTable.seats[i], i);

            if (i == Poker2Model.onTableData.myPlayerPos) {
                // if player this the user's player, show the hotspot animation
                this.showHotspotAnimation();
            }
        }

        // communitycards anzeigen
        if (currentTable.communityCards.length > 0) {
            this.onTableHelper.drawCommunityCards(currentTable.communityCards, this.aniHelper, true);
        }

        //TODO zweite Bedingung entfernen wenn Server mir eine -1 beim Dealer schickt
        if (currentTable.dealer != -1 && currentTable.playerTurn != -1)
            this.onTableHelper.drawDealer(currentTable.dealer, this.aniHelper);

        if (currentTable.playerTurn != -1)
            this.showPlayerStatus(currentTable.playerTurn, this.STATUS_PLAYER_TURN);

        this.onTableHelper.drawPot(currentTable.pot, this.aniHelper, false);
        this.setBlindText(currentTable.sBlind);
    }

    private adjustPotBoxColor(potBox: MovieClip, color: ColorTransform): void {
        potBox.outline.transform.colorTransform = color;
        potBox.pot_bg.transform.colorTransform = color;
        potBox.shadow_color_bottom.transform.colorTransform = color;

        potBox.outline.doCache();
        potBox.pot_bg.doCache();
        potBox.shadow_color_bottom.doCache();
    }

    /**
     * Community cards zeichnen und entsprechend einmal oder dreimal den Sound abspielen
     * LOG-Event schmeißen
     */
    private onCommunityCards(event: Poker2OnTableEvent): void {
        if (this.tutorialController.isActive) {
            switch (this.tutorialController.tutorialState) {
                case "tut_step16_pot":
                case "tut_step23_turn":
                case "tut_step27_river":
                    return;
                    break;
            }
        }
        if (Poker2Model.onTableData.communityCards == null)
            return;
        this.onTableHelper.drawCommunityCards(Poker2Model.onTableData.communityCards, this.aniHelper, true);

        if (Poker2Model.onTableData.communityCards.length <= 3) {
            this.controller.soundController.playSoundEffectByName(Poker2SoundController.SND_SPREAD, Poker2Model.onTableData.communityCards.length);
        } else {
            this.controller.soundController.playSoundEffectByName(Poker2SoundController.SND_SPREAD, 1);
        }

        this.controller.dispatchEvent(new Poker2ChatEvent("addlog", -1, -1, Poker2Model.languageData.getTextById("table_chatlog_communitycards_" + Poker2Model.onTableData.communityCards.length, this.getChatCardParams(Poker2Model.onTableData.communityCards)), "", false, false));
    }

    private reposPlayerCardHolder(itemholder: MovieClip): void {
        for (var i: number = 0; i < itemholder.numChildren; i++) {
            var child: MovieClip = castAs<MovieClip>(itemholder.getChildAt(i), "MovieClip");

            if (child)
                child.x += (i * (this.CARD_WIDTH - this.CARD_SPACE / 2)) - ((itemholder.numChildren - 1) * (this.CARD_WIDTH - this.CARD_SPACE / 2)) / 2;
        }
    }

    private drawPlayerGift(seatVO: SeatVO, fromIndex: number, toIndex: number): void {
        var fromPlayer: Library.PokerTable.PokerOnTable_Player = this.getPlayerContainer(fromIndex);
        var playerContainer: Library.PokerTable.PokerOnTable_Player = this.getPlayerContainer(toIndex);

        while (playerContainer.mc_giftholder.numChildren > 0)
            playerContainer.mc_giftholder.removeChildAt(0);

        if (seatVO.giftId > 0) {
            var giftVO: GiftsVO = Poker2Model.wodData.getItemVObyID(seatVO.giftId) as GiftsVO;
            var gift: MovieClip = AvatarHelper.drawGift(giftVO);
            playerContainer.mc_giftholder.addChild(gift);
            this.aniHelper.animationFromToPlayer(fromPlayer.mc_giftholder, playerContainer.mc_giftholder, gift);
        }
    }

    /**
     * Init PlayerContainer
     * benutzen wenn neuer Avatar am Tisch sitzt
     */
    private drawPlayerSeat(seatVO: SeatVO, index: number): void {
        var playerContainer: Library.PokerTable.PokerOnTable_Player = this.getPlayerContainer(index);

        // join button
        var joinButton: BasicButton = this.onTableScreen["btn_seat" + index] as BasicButton;
        playerContainer.properties = joinButton.properties = index;
        playerContainer.playerid = seatVO.playerId;
        playerContainer.visible = seatVO.seated;
        joinButton.visible = !playerContainer.visible;

        // create playerStateViewComponent
        if (!playerContainer.playerStateViewComponent) {
            playerContainer.playerStateViewComponent = new PlayerStateViewComponent(playerContainer.playerAction);
        }

        // create turn timer
        if (!playerContainer.turnTimer) {
            var tt: TurnTimerComponent = new TurnTimerComponent();
            tt.onTurnTimerCallbackTrigger = this.bindFunction(this.handleTurnTimerEvent);
            playerContainer.clockHolder.addChild(tt);
            playerContainer.turnTimer = tt;
        }

        // hide winning animation
        playerContainer.mc_winnanim.gotoAndStop(1);
        playerContainer.mc_winnanim.visible = false;

        if (seatVO.seated) {
            // draw Geschenk
            while (playerContainer.mc_giftholder.numChildren > 0)
                playerContainer.mc_giftholder.removeChildAt(0);

            if (seatVO.giftId > 0) {
                var giftVO: GiftsVO = Poker2Model.wodData.getItemVObyID(seatVO.giftId) as GiftsVO;
                playerContainer.mc_giftholder.addChild(AvatarHelper.drawGift(giftVO));
            }
            playerContainer.mc_giftholder.mouseChildren = false;
            playerContainer.mc_giftholder.seatId = index;
            playerContainer.btn_gift.seatId = index;
            playerContainer.btn_gift.toolTipText = Poker2Model.languageData.getTextById("table_gifts");

            playerContainer.mc_bg.mouseEnabled = false;
            playerContainer.mc_bg.uncache();
            //playerContainer.mc_holder.mouseChildren = false;
            playerContainer.mc_trophy.visible = seatVO.winTrophy;
            playerContainer.mc_bg.txt_name.text = seatVO.name;

            // evtl. ausgrauen
            this.checkZombieView(index, seatVO, playerContainer);
            // hader-state updaten
            this.updatePlayerContainer(index, seatVO);

            // draw Avatar
            while (playerContainer.mc_holder.numChildren > 0)
                playerContainer.mc_holder.removeChildAt(0);

            if (seatVO.stateGame != SeatVO.STATE_FREE) {
                /// var mcAvatar:Sprite = AvatarHelper.drawAvatarByItemArray( Poker2Model.avatarData.getAvatarClothesArray( seatVO.avatar ));
                var mcAvatar: Container = new AvatarHolder(seatVO.avatar, false) as Container;
                var bounds: Rectangle = mcAvatar.getBounds(null);

                mcAvatar.x = -((bounds.width * Poker2OnTableScreen.AVATAR_SCALE_FACTOR) / 2 + (bounds.left * Poker2OnTableScreen.AVATAR_SCALE_FACTOR));
                mcAvatar.y = -(bounds.top * Poker2OnTableScreen.AVATAR_SCALE_FACTOR + 5);
                mcAvatar.scaleX = mcAvatar.scaleY = Poker2OnTableScreen.AVATAR_SCALE_FACTOR;

                playerContainer.mc_holder.addChild(mcAvatar);
                playerContainer.mc_holder.doCache(0, 2);
            }
            // draw Einsatz
            this.onTableHelper.drawBet(index, seatVO.tableChips, this.aniHelper);
        }
    }

    private handleTurnTimerEvent(progress: number): void {
        if (this.currentPlayerSeat != Poker2Model.onTableData.myPlayerPos) {
            return;
        }

        switch (progress) {
            case 0.5:
                this.controller.soundController.playSoundEffectByName(Poker2SoundController.SND_TIME);
                break;

            case 1:
                this.controller.soundController.playSoundEffectByName(Poker2SoundController.SND_LATE);
                break;
        }
    }

    private checkZombieView(index: number, seatVO: SeatVO, playerContainer: Library.PokerTable.PokerOnTable_Player): void {
        // TODO: delete me
        //			if (Poker2Model.onTableData.currentTable.gameType != Poker2Constants.STANDARD_TABLE)

        if (!(instanceOfClass(Poker2Model.onTableData.currentTable.gameType, "DefaultTableTypeAbstract"))) {
            // Zombie abfrage bei tunieren
            if (seatVO.zombie == 1)
                this.aniHelper.grayscale(playerContainer);
            else {
                this.aniHelper.unGrayscale(playerContainer);

                if (index == Poker2Model.onTableData.myPlayerPos)
                    seatVO.stateGame = int(8);
            }
        }
        else {
            this.aniHelper.unGrayscale(playerContainer);
        }
    }

    private setPlayerActionbyState(state: number, playerContainer: Library.PokerTable.PokerOnTable_Player): void {
        var stateId: number = state;

        switch (state) {
            case 6:

                // show winning animation
                playerContainer.mc_winnanim.visible = true;

                // mc_winnanim was changed: the animation is now in one movieclip
                // needed to not interrupt the winning animation if only one player is left
                this._winAnimationIsPlaying = true;
                // create winning animation, play and store it
                var winAni: Poker2MovieClipHelper = new Poker2MovieClipHelper();
                winAni.playMovieClip(playerContainer.mc_winnanim, this.bindFunction(this.onPlayerActionStateWinAniComplete));
                this._winningAnimations.push(winAni);

                stateId = -1;
                break;
            case 1:

                this.aniHelper.animationFoldOnePlayer(playerContainer.mc_cardholder, this.onTableScreen.mc_table.mc_cardholder);
                break;

            default:

                break;
        }

        if (playerContainer.playerStateViewComponent) {
            ((playerContainer.playerStateViewComponent) as PlayerStateViewComponent).switchState(stateId);
        }
    }

    /**
     * TODO: This is an hotfix for following case:
     * Two Players are playing, one leaves the game while the game is running.
     * The other player wins and the win animation is playing. Generally in this case the win state will be resetted to waiting,
     * but if the round ends because a player left the game the win animation should't be interrupted.
     * The playerContainer will be resetted after the win animation has been finished.
     *
     * BUT: The orang box with the ChipsEarn belongs also the win animation, but is controlled in the "showWinners" method.
     * Refactor the winning animation in separate command to handle this correct.
     */
    private onPlayerActionStateWinAniComplete(target: MovieClip): void {
        target.visible = false;
        target.gotoAndStop(1);

        if (!this._winAnimationIsPlaying) {
            return;
        }

        this._winAnimationIsPlaying = false;

        if (this._resetPlayerActionState) {
            this._resetPlayerActionState = false;
            this.startIsPlayerIsLonelyTimer(1500);
        }
    }

    private showPlayerAction(playerActions: any[]): void {
        var action: number = int(playerActions[0]);
        var tableChips: number = int(playerActions[1]);
        var handChips: number = playerActions[2];
        var index: number = int(playerActions[3]);
        var seatVO: SeatVO = Poker2Model.onTableData.getSeatVOByIndex(index);
        seatVO.handChips = handChips;
        seatVO.stateGame = action;
        seatVO.tableChips = tableChips;
        this.updatePlayerContainer(index, seatVO);
        this.onTableHelper.drawBet(index, tableChips, this.aniHelper);

        this.controller.soundController.playSoundEffectByName(Poker2SoundController.soundByTableAction(action));

        if (action > 0)
            this.controller.dispatchEvent(new Poker2ChatEvent("addlog", seatVO.playerId, seatVO.userId, Poker2Model.languageData.getTextById("table_chatlog_" + action, [tableChips]), "", false, false));
    }

    /**
     * Update Level, Chips
     * Show winncards or rest Cardhold and hide clock
     * Benutzen wenn änderung des States (waiting, fold, check...) oder winncard-anzeige
     */
    private updatePlayerContainer(index: number, seatVO: SeatVO, winncards: any[] = null): void {
        var playerContainer: Library.PokerTable.PokerOnTable_Player = this.getPlayerContainer(index);

        while (playerContainer.mc_cardholder.numChildren > 0)
            playerContainer.mc_cardholder.removeChildAt(0);

        if (winncards) {
            var card1: Container = CardHelper.drawCard(seatVO.winnerC1, this.WINNER_CARD_HEIGHT, this.WINNER_CARD_WIDTH, winncards);
            var card2: Container = CardHelper.drawCard(seatVO.winnerC2, this.WINNER_CARD_HEIGHT, this.WINNER_CARD_WIDTH, winncards);
            playerContainer.mc_cardholder.addChild(card1);
            playerContainer.mc_cardholder.addChild(card2);
            this.reposPlayerCardHolder(playerContainer.mc_cardholder);
        }

        if (playerContainer.turnTimer) {
            playerContainer.turnTimer.stopTimer();
        }

        // hide mc_winnanim
        playerContainer.mc_winnanim.gotoAndStop(1);
        playerContainer.mc_winnanim.visible = false;

        this.setPlayerActionbyState(seatVO.stateGame, playerContainer);
        playerContainer.mc_bg.txt_cash.text = NumberStringHelper.groupString(seatVO.handChips, Poker2Model.languageData.getTextById.bind(Poker2Model.languageData));
        playerContainer.mc_bg.mc_xp.txt_level.text = String(seatVO.level);
    }

    private onShowRabbitCam(event: Poker2OnTableEvent): void {
        var gold: string;
        var goldFee: number = parseFloat(event.params[1]);
        if (!this._rabbitCam) {
            // create new rabbit cam component
            this._rabbitCam = new RabbitCam(this.aniHelper, goldFee);
            this.onTableScreen.addChild(this._rabbitCam);
        }

        // if user don't have enough gold show the rabbit cam but disabled
        if (goldFee > Poker2Model.userData.userGold) {
            // user has too low gold
            this._rabbitCam.disable();
        }
        else if (goldFee <= Poker2Model.userData.userGold) {
            // user have enough gold
            this._rabbitCam.enable();
        }

        // start rabbit cam timer (time comes from server)
        this._rabbitCam.startTimeout(event.params[0]);
    }

    private onHideRabbitCam(event: Poker2OnTableEvent): void {
        // stop and hide timer
        if (this._rabbitCam) {
            this._rabbitCam.hide();
        }
    }

    private onPlayRabbitCam(event: Poker2OnTableEvent): void {
        // sanity check if communityCards are defined
        if (!Poker2Model.onTableData.currentTable.communityCards || Poker2Model.onTableData.currentTable.communityCards.length == 0) {
            logger.error("onPlayRabbitCam(), communityCards not defined: " + Poker2Model.onTableData.currentTable.communityCards);
            return;
        }

        this.onTableHelper.drawCommunityCardsForRabbitCam(Poker2Model.onTableData.communityCards, this.aniHelper, true, 0.5);
    }

    private showShowdown(cards: ShowdownCardsVO[], communitycards: any[], winners: WinnerVO[], chipsearner: ChipsearnerVO[]): void {
        // cards == leer , alle haben gefoldet, bis auf einen
        // communitycards == 0; River
        // communitycards > 0; Allin
        if (cards.length > 0) {
            if (communitycards.length == 0) {
                // communitycards == 0; River
                // Karten aufdecken 1s delay
                this.currentIndexCounter = 0;
                this.showdownTimer = new Timer(1000, cards.length);
                this.showdownTimer.addEventListener(TimerEvent.TIMER, this.bindFunction(this.showPlayerCards));
                this.showdownTimer.addEventListener(TimerEvent.TIMER_COMPLETE, this.bindFunction(this.onShowPlayerCardsComplete));
                this.showdownTimer.start();
            }
            else {
                this.currentIndexCounter = 0;
                this.showdownTimer = new Timer(1000, cards.length);
                this.showdownTimer.addEventListener(TimerEvent.TIMER, this.bindFunction(this.showPlayerCards));
                this.showdownTimer.addEventListener(TimerEvent.TIMER_COMPLETE, this.bindFunction(this.onShowAllinComplete));
                this.showdownTimer.start();
                // communitycards > 0; Allin
            }
        }
        else {
            this.currentIndexCounter = 0;
            this.showWinners(Poker2Model.onTableData.showdownWinner, Poker2Model.onTableData.showdownChips, false);
            //alle fold bis auf einen
        }
    }

    private onShowAllinComplete(event: TimerEvent): void {
        if (!this.showdownTimer) return;
        this.showdownTimer.removeEventListener(TimerEvent.TIMER, this.bindFunction(this.showPlayerCards));
        this.showdownTimer.removeEventListener(TimerEvent.TIMER_COMPLETE, this.bindFunction(this.onShowAllinComplete));
        this.showdownTimer.stop();
        this.showdownTimer = null;
        if (this.tutorialController.isActive) {
            this.onTableHelper.clearCommunityCards();
        }
        this.onTableHelper.drawCommunityCards(Poker2Model.onTableData.communityCards, this.aniHelper, true, 0.5, true);
        var delay: number = int(Poker2Model.onTableData.showdownCommunitycards.length * 2000);
        this.showdownTimer = new Timer(delay, 1);
        this.showdownTimer.start();
        this.showdownTimer.addEventListener(TimerEvent.TIMER_COMPLETE, this.bindFunction(this.onShowPlayerCardsComplete));
    }

    private onShowPlayerCardsComplete(event: TimerEvent): void {
        if (!this.showdownTimer) return;
        this.showdownTimer.removeEventListener(TimerEvent.TIMER, this.bindFunction(this.showPlayerCards));
        this.showdownTimer.removeEventListener(TimerEvent.TIMER_COMPLETE, this.bindFunction(this.onShowPlayerCardsComplete));
        this.showdownTimer.stop();
        this.showdownTimer = null;
        if (this.tutorialController.isActive) {
            this.onTableHelper.clearCommunityCards();
        }
        this.showWinners(Poker2Model.onTableData.showdownWinner, Poker2Model.onTableData.showdownChips);
    }

    private showPlayerCards(event: TimerEvent): void {
        //sanity check
        if (!Poker2Model.onTableData.showdownCards) {
            return;
        }

        var counter: number = this.currentIndexCounter++;
        var cards: ShowdownCardsVO = Poker2Model.onTableData.showdownCards[counter];
        if (!cards) return;

        var playerContainer: Library.PokerTable.PokerOnTable_Player = this.getPlayerContainer(cards.index);

        while (playerContainer.mc_cardholder.numChildren > 0)
            playerContainer.mc_cardholder.removeChildAt(0);

        if (cards.c1 != "") {
            var card1: Container = CardHelper.drawCard(cards.c1, this.WINNER_CARD_HEIGHT, this.WINNER_CARD_WIDTH);
            var card2: Container = CardHelper.drawCard(cards.c2, this.WINNER_CARD_HEIGHT, this.WINNER_CARD_WIDTH);
            playerContainer.mc_cardholder.addChild(card1);
            playerContainer.mc_cardholder.addChild(card2);
            this.reposPlayerCardHolder(playerContainer.mc_cardholder);
        }
        else {
            this.aniHelper.animationFoldOnePlayer(playerContainer.mc_cardholder, this.onTableScreen.mc_table.mc_cardholder);
        }
    }

    private showWinners(winners: WinnerVO[], chipsearner: ChipsearnerVO[], showHand: boolean = true): void {
        var delay: number = 4;
        var pot: number = int(this.cashPot);

        if (winners && winners.length > 0) {
            if (winners != null) {
                for (let entryList of winners) {
                    var winner: any = entryList;
                    if (winner === undefined) continue;

                    var playerContainer: Library.PokerTable.PokerOnTable_Player = this.getPlayerContainer(winner.index);
                    var seatVO: SeatVO = Poker2Model.onTableData.getSeatVOByIndex(winner.index);
                    seatVO.stateGame = int(6);
                    if (winner.chips)
                        seatVO.handChips += winner.chips;
                    if (showHand) {
                        seatVO.winnerC1 = winner.c1;
                        seatVO.winnerC2 = winner.c2;
                        var highlightCards: any[] = Poker2Model.onTableData.highlightWinnerCards(winner);
                        this.updatePlayerContainer(winner.index, seatVO, highlightCards);
                        this.onTableHelper.drawCommunityCards(Poker2Model.onTableData.communityCards, this.aniHelper, true, 2, false, highlightCards);
                    } else {
                        this.updatePlayerContainer(winner.index, seatVO);
                    }

                    if (winner.index == Poker2Model.onTableData.myPlayerPos) {
                        this.controller.soundController.playSoundEffectByName(Poker2SoundController.SND_WIN);
                    }

                    this.controller.soundController.playSoundEffectByName(Poker2SoundController.SND_CHIPS);
                    //					controller.dispatchEvent( new Poker2ChatEvent( "addlog", seatVO.playerId, seatVO.userId, Poker2Model.languageData.getTextById( "table_chatlog_win", [ seatVO.name, winner.chips ]), seatVO.name, false, false ));

                    if (winner.handcategory >= 0 && showHand)
                        this.controller.dispatchEvent(new Poker2ChatEvent("addlog", seatVO.playerId, seatVO.userId, Poker2Model.languageData.getTextById("table_chatlog_wincard_" + winner.handcategory, this.getChatCardParams(highlightCards)), "", false, false));
                }
            }

            for (let entryList of Poker2Model.onTableData.showdownChips) {
                var chipsEarn: any = entryList;
                if (chipsEarn === undefined) continue;

                var playerChipsEarner: Library.PokerTable.PokerOnTable_Player = this.getPlayerContainer(chipsEarn.index);
                var seatEarner: SeatVO = Poker2Model.onTableData.getSeatVOByIndex(chipsEarn.index);
                pot -= int(chipsEarn.chips);

                this.controller.dispatchEvent(new Poker2ChatEvent("addlog", seatEarner.playerId, seatEarner.userId, Poker2Model.languageData.getTextById("table_chatlog_win", [seatEarner.name, chipsEarn.chips]), seatEarner.name, false, false));

                if (this.onTableScreen.mc_table.mc_pot.mc_holder.numChildren > 0) {
                    var currentPotChips: Library.PokerTable.Chips = this.onTableScreen.mc_table.mc_pot.mc_holder.getChildAt(0) as Library.PokerTable.Chips;
                    this.onTableScreen.mc_table.mc_pot.txt_amount.text = (pot > 0) ? NumberStringHelper.groupString(pot, Poker2Model.languageData.getTextById.bind(Poker2Model.languageData)) : "";
                    this.aniHelper.setChipsWert2Chips(currentPotChips, pot);
                }
                var chips: Library.PokerTable.Chips = new Library.PokerTable.Chips();
                chips.scaleX = chips.scaleY = this.CHIP_SCALE;
                this.aniHelper.setChipsWert2Chips(chips, chipsEarn.chips);
                this.onTableScreen.mc_table.mc_pot.mc_holder.addChild(chips);

                // TODO: orange box with win value
                //					trace("___ANIMATE ORANGE WIN BOX -> chipsEarn.chips: " + chipsEarn.chips + " chips: " + chips);
                this.aniHelper.animationToOnewinner(this.onTableScreen.mc_table.mc_pot, playerChipsEarner, chips, chipsEarn.chips, delay / 2);
                playerChipsEarner.mc_bg.txt_cash.text = NumberStringHelper.groupString(chipsEarn.chips, Poker2Model.languageData.getTextById.bind(Poker2Model.languageData));
                delay++;
            }
            this.blockDialogTimer = new Timer((delay * 1000) + this.TWO_SEC_DELAY, 1);
            this.blockDialogTimer.start();
            this.blockDialogTimer.addEventListener(TimerEvent.TIMER_COMPLETE, this.bindFunction(this.onShowDownComplete));

            // check if winner is lonely on the table
            // TODO:
            this.startIsPlayerIsLonelyTimer();
        }
        else {
            for (var i: number = 0; i < Poker2Model.onTableData.playersSeatIndex.length; i++) {
                var seat: SeatVO = Poker2Model.onTableData.getSeatVOByIndex(Poker2Model.onTableData.playersSeatIndex[i]);
                seat.stateGame = int(8);
                this.updatePlayerContainer(Poker2Model.onTableData.playersSeatIndex[i], seat);
                this.onTableHelper.clearTable();
            }
        }
    }

    private onShowDownComplete(event: TimerEvent): void {
        BasicDialogHandler.getInstance().blockDialogs = false;
        //TODO hier koennte man den Tisch resetten, dazu muß das GENCommand erweitertwerden um die handchips des Spielers
        /*				onTableHelper.clearTable();
                        resetPlayerContainers( Poker2Model.onTableData.playersSeatIndex );*/
        if (this.blockDialogTimer) {
            this.blockDialogTimer.stop();
            this.blockDialogTimer = null;
        }

        if (Poker2Model.onTableData.currentTable) {
            Poker2Model.onTableData.currentTable.showdownAnimationInProgress = false;
        }
        this.controller.dispatchEvent(new Poker2OnTableEvent("showdownAnimationComplete"));
    }

    private getChatCardParams(cards: any[]): any[] {
        var result: any[] = [];

        for (var i: number = 0; i < cards.length; i++) {
            var cardString: string = cards[i];

            if (cardString)
                result.push(Poker2Model.languageData.getTextById("card_" + cardString.charAt(1)) + cardString.charAt(0));
        }
        return result;
    }

    private hidePlayerSeat(index: number): void {
        var playerContainer: Library.PokerTable.PokerOnTable_Player = this.getPlayerContainer(index);
        var joinButton: BasicButton = this.onTableScreen["btn_seat" + index] as BasicButton;
        playerContainer.visible = false;
        joinButton.visible = !playerContainer.visible;
    }

    /**
     * Alle auf STILL-Waiting setzen
     */
    private resetPlayerContainers(players: any[]): void {
        for (var i: number = 0; i < players.length; i++) {
            var seatVO: SeatVO = Poker2Model.onTableData.getSeatVOByIndex(players[i]);
            seatVO.stateGame = int(8);
            this.updatePlayerContainer(players[i], seatVO);
        }
    }

    private showPlayerStatus(index: number, status: number): void {
        var playerContainer: Library.PokerTable.PokerOnTable_Player = this.getPlayerContainer(index);
        var seatVO: SeatVO = Poker2Model.onTableData.getSeatVOByIndex(index);

        if (status == this.STATUS_PLAYER_TURN) {
            this.currentPlayerSeat = index;
            if (playerContainer.turnTimer) {
                playerContainer.turnTimer.startTimer(Poker2Model.onTableData.currentTable.actionTime);
            }
            seatVO.stateGame = int(7);
            ((playerContainer.playerStateViewComponent) as PlayerStateViewComponent).switchState(7);
        }
    }

    /**
     * de/-aktiviert die take-a-seat-Button
     */
    private joinButtonHelper(state: boolean): void {
        for (var i: number = 0; i < 9; i++) {
            var joinButton: BasicButton = this.onTableScreen["btn_seat" + i] as BasicButton;
            joinButton.label = Poker2Model.languageData.getTextById("btn_seat_free");
            joinButton.enableButton = state;
            if (state) {
                joinButton.toolTipText = Poker2Model.languageData.getTextById("btn_seat_free_tooltip");
            }
            else {
                joinButton.toolTipText = null;
            }
        }
    }

    private checkIfBooked(bookedSeats: any[]): void {
        for (var i: number = 0; i < bookedSeats.length; i++) {
            var joinButton: BasicButton = this.onTableScreen["btn_seat" + bookedSeats[i]] as BasicButton;
            joinButton.label = Poker2Model.languageData.getTextById("btn_seat_reserved");
            joinButton.enableButton = false;
            joinButton.toolTipText = Poker2Model.languageData.getTextById("btn_seat_reserved_tooltip");
        }
    }

    private disableSeat(seatId: number): void {
        var joinButton: BasicButton = this.onTableScreen["btn_seat" + seatId] as BasicButton;
        joinButton.label = Poker2Model.languageData.getTextById("btn_seat_reserved");
        joinButton.enableButton = false;
        joinButton.toolTipText = Poker2Model.languageData.getTextById("btn_seat_reserved_tooltip");
    }

    private releaseSeat(seatId: number): void {
        var joinButton: BasicButton = this.onTableScreen["btn_seat" + seatId] as BasicButton;
        joinButton.label = Poker2Model.languageData.getTextById("btn_seat_free");
        joinButton.toolTipText = Poker2Model.languageData.getTextById("btn_seat_free_tooltip");
        joinButton.enableButton = true;
        joinButton.toolTipText = null;
    }

    private onChangeLayoutState(event: Poker2DialogEvent): void {
        if (event.params[1] == 10 && event.params[0] != 1) {
            BasicDialogHandler.getInstance().blockDialogs = false;
            BasicModel.smartfoxClient.sendMessage("tle", []);
            this.controller.dispatchEvent(new Poker2AntiCheatEvent("antiCheatRemoveConnection"));
        }
    }

    private getPlayerContainer(index: number): Library.PokerTable.PokerOnTable_Player {
        var playerContainer: Library.PokerTable.PokerOnTable_Player = this.onTableScreen["mc_player" + index] as Library.PokerTable.PokerOnTable_Player;
        return playerContainer;
    }

    private getAvatarCard(playerId: number): void {
        var paramObject: any = { id: playerId, viewId: 1 };
        BasicModel.smartfoxClient.sendMessage("gpv", [JSON.stringify(paramObject)]);
    }

    private bookSeat(seatIndex: number): void {
        var paramObject: any = { seatId: seatIndex };
        BasicModel.smartfoxClient.sendMessage("gbs", [JSON.stringify(paramObject)]);
    }

    public get onTableScreen(): Library.PokerTable.PokerOnTable {
        return this.disp as Library.PokerTable.PokerOnTable;
    }

    public static __initialize_static_members() {
        Poker2OnTableScreen.STANDARD_TABLE_GREEN = new ColorTransform(0, 0, 0, 1, 57, 110, 21, 0);
        Poker2OnTableScreen.TOURNAMENT_TABLE_BLUE = new ColorTransform(0, 0, 0, 1, 66, 124, 179, 0);
        Poker2OnTableScreen.SITNGO_TABLE_GREY = new ColorTransform(0, 0, 0, 1, 69, 69, 69, 0);
        Poker2OnTableScreen.STANDARD_TABLE_GREEN_TXT_FORMAT = new TextFormat(null, null, 0x143000);
        Poker2OnTableScreen.TOURNAMENT_TABLE_BLUE_TXT_FORMAT = new TextFormat(null, null, 0x114893);
        Poker2OnTableScreen.SITNGO_TABLE_GREY_TXT_FORMAT = new TextFormat(null, null, 0x2A2A2A);
        Poker2OnTableScreen.FALLBACK_FOR_SMALL_RESOLUTION_SCREENSIZE_TXT_FORMAT = new TextFormat(null, null, 0xffffff);
        Poker2OnTableScreen.XMAS_TABLE_TXT_FORMAT = new TextFormat(null, null, 0x290F0F);
    }
}

import { Poker2Constants } from "../../constants/Poker2Constants";
import { PokerGameTypes } from "../../constants/PokerGameTypes";

import { Poker2TutorialController } from "../../controller/Poker2TutorialController";

import { Poker2TableAvatarDialog } from "../dialogs/onTable/Poker2TableAvatarDialog";
import { Poker2TakeSeatDialog } from "../dialogs/onTable/Poker2TakeSeatDialog";
import { Poker2FadingDialog } from "../dialogs/Poker2FadingDialog";
import { Poker2SendGiftDialog } from "../dialogs/Poker2SendGiftDialog";

import { AniTableHelper } from "../helper/AniTableHelper";
import { AvatarHelper } from "../helper/AvatarHelper";
import { AvatarHolder } from "../helper/AvatarHolder";
import { OnTableHelper } from "../helper/OnTableHelper";

import { Poker2MenuPanel } from "../panels/Poker2MenuPanel";

import { Poker2LayoutManager } from "../Poker2LayoutManager";

import { HotspotAniHelperController } from "./onTable/animationHelper/HotspotAniHelperController";

import { RabbitCam } from "./onTable/animationHelper/RabbitCam";

import { castAs } from "@goodgamestudios/createjs-utilities";
Poker2OnTableScreen.__initialize_static_members();

import { instanceOfClass } from "@goodgamestudios/createjs-utilities";
